import { Auth0ContextInterface, User } from '@auth0/auth0-react';
import { Observable, Subscription } from 'rxjs';
import { InMemoryCacheData } from '@ynomia/client';
import { StoredTokens, hasNewerTokens, readStoredTokens } from '@ynomia/client-auth';
import {
  ACCESS_TOKEN_STORAGE_KEY,
  EXPIRED,
  ID_TOKEN_STORAGE_KEY,
  REFRESH_TOKEN_STORAGE_KEY,
} from '../../config/constants';
import { analytics } from '..';
import auth0Cache from './Auth0Cache';
import client from '.';
import config from '../../config';

const SESSION_RESTORED_AT_STORAGE_KEY = 'session_restored_at';
const SESSION_RESTORE_INTERVAL_MS = 1000 * 60;

interface AuthObservables {
  onChange$: Observable<InMemoryCacheData>
  onEstablish$: Observable<InMemoryCacheData>
  onRefresh$: Observable<InMemoryCacheData>
  onRefreshFailure$: Observable<boolean>
  onTerminate$: Observable<InMemoryCacheData>
}

/**
 * This module listens for changes in the authentication status of the current user.
 */
class AuthService {
  private onChangeSubscription?: Subscription;

  private onEstablishSubscription?: Subscription;

  private onRefreshSubscription?: Subscription;

  private onRefreshFailureSubscription?: Subscription;

  private onTerminateSubscription?: Subscription;

  private static auth0: Auth0ContextInterface<User> | undefined;

  /**
   * The last tokens this app held. Destroying a session wipes them from the cache before
   * `onTerminate` runs, which needs them to tell whether another app has moved on since.
   */
  private static lastKnownTokens: StoredTokens | undefined;

  /**
   * Use this method after establishing a new `SessionManager` instance for `@ynomia/client` to
   * subscribe this observer class to the Auth Observables provided.
   * @param {AuthObservables} authObservables
   * @return {void}
   */
  initialize(
    {
      onChange$, onEstablish$, onRefresh$, onTerminate$, onRefreshFailure$,
    }: AuthObservables,
    auth0: Auth0ContextInterface<User>,
  ): void {
    client.cache.update({ auth: { domain: config.host.api } });

    if (onChange$) {
      this.onChangeSubscription?.unsubscribe();
      this.onChangeSubscription = onChange$.subscribe(AuthService.rememberTokens);
    }

    if (onEstablish$) {
      this.onEstablishSubscription?.unsubscribe();
      this.onEstablishSubscription = onEstablish$.subscribe(AuthService.onEstablish);
    }

    if (onRefresh$) {
      this.onRefreshSubscription?.unsubscribe();
      this.onRefreshSubscription = onRefresh$.subscribe(AuthService.onRefresh);
    }

    if (onRefreshFailure$) {
      this.onRefreshFailureSubscription?.unsubscribe();
      this.onRefreshFailureSubscription = onRefreshFailure$.subscribe(AuthService.onRefreshFailure);
    }

    if (onTerminate$) {
      this.onTerminateSubscription?.unsubscribe();
      this.onTerminateSubscription = onTerminate$.subscribe(AuthService.onTerminate);
    }

    if (auth0) {
      AuthService.auth0 = auth0;
    }
  }

  /**
   * @event onEstablish
   * Emits when a brand new user session is established. This happens typically after a user has
   * just signed in. Note that this event is NOT emitted when restoring existing sessions that
   * have been initialized from local storage data.
   * @param {InMemoryCacheData} clientCache
   * @return {void}
   */
  private static onEstablish(clientCache: InMemoryCacheData): void {
    AuthService.persistAuthDataOffline(clientCache);
  }

  /**
   * @event onRefresh
   * Emits each time a new access token is attained using the user's refresh token.
   * @param {InMemoryCacheData} clientCache
   * @return {void}
   */
  private static onRefresh(clientCache: InMemoryCacheData): void {
    AuthService.persistAuthDataOffline(clientCache);
  }

  /**
   * @event onRefreshFailure
   * Emits when a token refresh fails. At this point, a logout event could be imminent, so this
   * is our last chance to do something before this happens.
   * @return {void}
   */
  private static onRefreshFailure(): void {
    // Event tracking
    analytics.trackEvent('JWT Refresh Failure');
    // Another app or tab on this origin may have refreshed while this one was failing. The
    // coordinated refresh normally adopts its tokens before this app's refresh reaches the
    // network; this covers the cases it could not. Only a newer token set for the same user is
    // adopted, never an empty one or another user's. If the next request still fails, a final
    // logout event will be fired by the Session Manager.
    const stored = readStoredTokens();
    if (!hasNewerTokens(AuthService.tokensOf(client.cache.current), stored)) return;
    client.cache.update({
      auth: {
        accessToken: stored.accessToken,
        refreshToken: stored.refreshToken || undefined,
        idToken: stored.idToken || undefined,
      },
    });
    try {
      // Keep the user's details in step with the adopted tokens
      client.session.decodeUserInfo();
    } catch {
      // A malformed token must not undo the adoption
    }
  }

  /**
   * @event onTerminate
   * Emits when a previously authenticated user session has been terminated. This can occur for
   * a number of reasons, including token expiry (with no successful refresh) and the session
   * being manually destroyed (e.g. when signing out).
   * @param {InMemoryCacheData} clientCache
   * @return {void}
   */
  private static onTerminate(clientCache: InMemoryCacheData): void {
    const { unauthenticatedReason } = clientCache.auth;

    // This app's session expired, but another app or tab on this origin may hold a newer
    // session for the same user, e.g. when this app's refresh lost a race with it. Clearing the
    // shared tokens and signing out of Auth0 would end that session for every app, so reload
    // into it instead, at most once a minute so it can never become a reload loop.
    if (
      unauthenticatedReason === EXPIRED
      && AuthService.lastKnownTokens
      && hasNewerTokens(AuthService.lastKnownTokens, readStoredTokens())
      && AuthService.claimSessionRestore()
    ) {
      analytics.trackEvent('JWT Session Restored');
      window.location.reload();
      return;
    }

    // Event tracking
    analytics.trackEvent('User Logout', { reason: unauthenticatedReason });

    localStorage.removeItem(ACCESS_TOKEN_STORAGE_KEY);
    localStorage.removeItem(ID_TOKEN_STORAGE_KEY);
    localStorage.removeItem(REFRESH_TOKEN_STORAGE_KEY);

    AuthService.auth0?.logout({
      logoutParams: {
        returnTo: `${window.location.origin}/login?unauthenticatedReason=${unauthenticatedReason}`,
      },
    });
  }

  /**
   * True at most once a minute per tab. The time is kept in session storage, which survives the
   * reload and belongs to this tab alone.
   * @return {boolean}
   */
  private static claimSessionRestore(): boolean {
    try {
      const restoredAt = Number(sessionStorage.getItem(SESSION_RESTORED_AT_STORAGE_KEY));
      if (Date.now() - restoredAt < SESSION_RESTORE_INTERVAL_MS) return false;
      sessionStorage.setItem(SESSION_RESTORED_AT_STORAGE_KEY, String(Date.now()));
      return true;
    } catch {
      // Without session storage there is no way to tell a loop apart, so sign out as before
      return false;
    }
  }

  /**
   * Keeps a copy of the tokens this app holds, for `onTerminate`.
   * @param {InMemoryCacheData} clientCache
   * @return {void}
   */
  private static rememberTokens(clientCache: InMemoryCacheData): void {
    if (clientCache.auth.accessToken) {
      AuthService.lastKnownTokens = AuthService.tokensOf(clientCache);
    }
  }

  /**
   * @param {InMemoryCacheData} clientCache
   * @return {StoredTokens}
   */
  private static tokensOf({ auth }: InMemoryCacheData): StoredTokens {
    return {
      accessToken: auth.accessToken ?? '',
      idToken: auth.idToken ?? '',
      refreshToken: auth.refreshToken ?? '',
    };
  }

  /**
   * Saves mission critical authentication data offline so that sessions can be restored after
   * refreshing the browser.
   * @param {InMemoryCacheData} clientCache
   * @return {void}
   */
  private static persistAuthDataOffline(clientCache: InMemoryCacheData): void {
    AuthService.saveOffline(ACCESS_TOKEN_STORAGE_KEY, clientCache.auth.accessToken);
    AuthService.saveOffline(ID_TOKEN_STORAGE_KEY, clientCache.auth.idToken);
    AuthService.saveOffline(REFRESH_TOKEN_STORAGE_KEY, clientCache.auth.refreshToken);
  }

  /**
   * Saves data offline into local storage, but only if the value exists.
   * @param {string} key
   * @param {string?} value
   * @return {void}
   */
  private static saveOffline(key: string, value?: string): void {
    if (value) {
      localStorage.setItem(key, value);
    }
  }

  /**
   * Opens Auth0 in an in-app-browser and prompts users to sign in. When successful, a
   * bootstrap request is also made to the Ynomia backend to refresh the latest project data.
   * @return {Promise<void>}
   */
  // eslint-disable-next-line class-methods-use-this
  async login(): Promise<void> {
    await client.session.authenticate({
      getTokens: async () => {
        const { body } = auth0Cache.data || {};
        return {
          accessToken: body?.access_token || '',
          idToken: body?.id_token || '',
          refreshToken: body?.refresh_token || '',
        };
      },
    });
  }
}

const authService = new AuthService();

export default authService;
